import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, MenuItem, TextField } from "@mui/material";
// eslint-disable-next-line import/no-extraneous-dependencies
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import classNames from "classnames";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import {
    getProductDetail,
    listProductPdpUrls,
    createProductPdpUrl,
    productPdpUrlVideo,
    getFileIconikJsonLd,
    generateProductPdpUrlJsonLd,
    requestProductPdpUrlScore,
    rescoreProductPdpUrl
} from "../../../../api/endpoints";
import {
    IconGoToTab,
    IconLink,
    IconPlus,
    IconProduct,
    IconVideo
} from "../../../../utils/icons";
import { DotLottleLoader } from "../../../../components/Loader";
import { JsonLdValidation } from "../../../../components/JsonLdEligibility/JsonLdEligibility";
import { PageVideo, PdpJsonLdOutput, PdpUrlVideoLink } from "../../../../components/PdpPages/PdpJsonLdOutput";
import { FieldLabel, PDP_FIELD_HELP } from "../../../../components/PdpPages/FieldLabel";
import { PdpScoreHistory } from "../../../../components/PdpScoreStatus/PdpScoreHistory";
import { PdpScoreReportDialog } from "../../../../components/PdpScoreStatus/PdpScoreReportDialog";
import {
    brandOptionLabel,
    ProductBrand,
    usePdpUrlBrand
} from "../../productsV2/productModal/ProductPdpUrlsPanel";
import { IBrand } from "../../../brand/team/data/type";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import {
    PdpScoreStatus,
    PdpUrlAttempt,
    PdpUrlScore,
    usePendingScorePoll
} from "../../../../components/PdpScoreStatus/PdpScoreStatus";
import { defaultPageIndex, linkFor, pageAssignment } from "./pdpAssignment";
import { AssignPageBlock, AssignmentChip, OtherVideoSummary } from "./AssignPage";
import { SkuPdpLinkEditor } from "../../../../components/SkuLinks/SkuPdpLinkEditor";
import { markupSignature, watchMarkupChange } from "../../../../components/PdpPages/markupWatch";
import { PdpPageSummary } from "../../../../components/PdpPages/PdpPageSummary";
import { PdpChecks } from "../../../../components/PdpPages/PdpChecks";

interface PdpUrl {
    id: number;
    pdp_url: string;
    retailer?: string | null;
    /**
     * Our videos on the page, one link each (a page carries any number,
     * TDC-1644). This video's link — file URL, overrides, VideoObject — is
     * what the form below edits; the others name whose page it also is.
     */
    videos?: PdpUrlVideoLink[] | null;
    /** Deactivated rows still list, but the cron — and JSON-LD generation — skip them. */
    active?: boolean | null;
    product_json_ld?: any;
    product_json_ld_validation?: JsonLdValidation | null;
    page_videos?: PageVideo[] | null;
    skus?: string[];
    latest_score?: PdpUrlScore | null;
    latest_attempt?: PdpUrlAttempt | null;
    // Shown by the shared page summary (PdpPageSummary), as on Product Pages.
    score_interval?: string | null;
    autopilot?: boolean;
    checks?: PdpChecks | null;
}

const EMPTY_OVERRIDES = {
    thumbnail_url_override: "",
    upload_date_override: ""
};

const OVERRIDE_FIELDS: {
    field: keyof typeof EMPTY_OVERRIDES;
    label: string;
    jsonLdKey: "thumbnailUrl" | "uploadDate";
    isDate?: boolean;
    help: string;
}[] = [
    { field: "thumbnail_url_override", label: "Thumbnail URL", jsonLdKey: "thumbnailUrl", help: PDP_FIELD_HELP.thumbnailUrl },
    { field: "upload_date_override", label: "Upload Date", jsonLdKey: "uploadDate", isDate: true, help: PDP_FIELD_HELP.uploadDate }
];

// The overrides form's values for THIS video's link on the page.
const overridesFromLink = (link?: PdpUrlVideoLink | null) =>
    OVERRIDE_FIELDS.reduce((acc, { field, jsonLdKey, isDate }) => {
        // Override wins; else our derived default (what a regenerate would
        // use); else whatever the stored blob has.
        const value =
            link?.[field] || link?.json_ld_defaults?.[jsonLdKey] || link?.json_ld?.[jsonLdKey] || "";
        return { ...acc, [field]: isDate ? String(value).slice(0, 10) : String(value) };
    }, EMPTY_OVERRIDES);

interface Props {
    video: any;
    brand: IBrand;
}


export function PdpUrlsManager({ video, brand }: Props) {
    const { role } = useUser();
    const products = useMemo(() => (video?.videoProducts || []).filter((p: any) => p?.desireid), [video]);
    const brandId = brand?.id;
    // The product's brands, for the new-row brand choice (the video payload
    // carries products without their brands).
    const [productBrands, setProductBrands] = useState<ProductBrand[]>([]);
    const pdpBrand = usePdpUrlBrand(productBrands);
    // Internal, outside a workspace: the operator picks the page's brand; a
    // product with no brands at all falls back to the video's brand so the
    // add still works.
    const newRowBrandId = pdpBrand.brandId || (pdpBrand.options.length === 0 ? brandId : null);

    const [productIdx, setProductIdx] = useState(0);
    const [pdpUrls, setPdpUrls] = useState<PdpUrl[]>([]);
    const [loading, setLoading] = useState(false);
    const [pdpIdx, setPdpIdx] = useState(0);
    const [pdpUrlDraft, setPdpUrlDraft] = useState("");
    const [videoFileUrlDraft, setVideoFileUrlDraft] = useState("");
    const [overrideDrafts, setOverrideDrafts] = useState(EMPTY_OVERRIDES);
    const [skusDraft, setSkusDraft] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [rescoring, setRescoring] = useState(false);
    const [generatingJsonLd, setGeneratingJsonLd] = useState(false);
    const [generatingProduct, setGeneratingProduct] = useState(false);
    // A past run picked from the score history — same dialog the product panel opens.
    const [viewingReport, setViewingReport] = useState<{ pdpUrlId: number; scoreId: number; pdpUrl: string } | null>(
        null
    );
    // Saving this video's link with a file URL makes the API generate its
    // JSON-LD in the background (fire-and-forget server-side) — this watch
    // polls the list until the link's json_ld appears/changes so the UI
    // reflects it.
    const [autoGenWatch, setAutoGenWatch] = useState<{ rowId: number; prev: string } | null>(null);
    // After an analysis: poll until the row's markup (re-detected page
    // videos, regenerated VideoObjects / Product Object) lands — it is
    // rebuilt in the background after the score returns.
    const markupWatchCancel = useRef<(() => void) | null>(null);
    useEffect(() => () => markupWatchCancel.current?.(), []);

    const product = products[productIdx];
    const desireid = product?.desireid ? Number(product.desireid) : null;
    // Same product deep-link the video cards use: internal staff land on
    // the brand-scoped admin product page, brand users on their own list.
    let productHref: string | null = null;
    if (desireid) {
        productHref =
            isInternal(role) && brandId
                ? `/products/${brandId}?productId=${desireid}`
                : `/products?productId=${desireid}`;
    }
    const isAddTab = pdpIdx >= pdpUrls.length;
    const current = pdpUrls[pdpIdx];
    // Whose page the selected tab is: drives the chip, the Assign block, and the
    // read-only summary for a page only other videos are on.
    const assignment = current ? pageAssignment(current, video?.id) : null;
    // This video's link on the selected page — the file URL / overrides /
    // VideoObject the form edits. Null on the Add tab and on pages it is not on.
    const currentLink = linkFor(current, video?.id);
    const linkJson = (row: PdpUrl | null | undefined) => JSON.stringify(linkFor(row, video?.id)?.json_ld ?? null);

    const fetchPdpUrls = async (did: number): Promise<PdpUrl[] | null> => {
        setLoading(true);
        try {
            // No brandId filter: a network video sits on BOTH the retailer and
            // the supplier brand, and the modal resolves the first one, so a
            // brand-scoped list dropped the supplier's rows (the product
            // panel lists unscoped too). Brand users are still limited to
            // their accessible brands server-side.
            const { data } = await axiosInstant.get(listProductPdpUrls(did));
            // EVERY page on the product, whoever holds it — the tab strip is the
            // picker. Each tab says whose the page is (AssignmentChip); another
            // video's page is read-only here and can be moved to this video.
            const rows: PdpUrl[] = Array.isArray(data) ? data : data?.data || [];
            setPdpUrls(rows);
            // Land on this video's page, else the first unassigned one — never on
            // a read-only tab while there is something to act on.
            setPdpIdx(defaultPageIndex(rows, video?.id));
            return rows;
        } catch (e) {
            toast.error("Couldn't load PDP URLs. Please try again.");
            setPdpUrls([]);
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Background refresh (score polling): same scoping as fetchPdpUrls but
    // swaps rows in place — no loader, and the selected PDP tab is kept.
    const refreshPdpUrlsSilently = async (): Promise<PdpUrl[] | null> => {
        if (!desireid) return null;
        try {
            const { data } = await axiosInstant.get(listProductPdpUrls(desireid));
            const rows: PdpUrl[] = Array.isArray(data) ? data : data?.data || [];
            setPdpUrls(rows); // row order (id ASC) is stable, so pdpIdx stays valid
            return rows;
        } catch (e) {
            // transient — the next poll tick retries
            return null;
        }
    };

    // Refetch in place and stay on the same row by id — the list can grow or
    // reorder underneath us. Returns the row as the server now has it.
    const refetchAndLand = async (pageId: number): Promise<PdpUrl | null> => {
        const rows = await refreshPdpUrlsSilently();
        if (!rows) return null;
        const idx = rows.findIndex((r) => r.id === pageId);
        if (idx >= 0) setPdpIdx(idx);
        return idx >= 0 ? rows[idx] : null;
    };

    // A freshly added URL is scored asynchronously (the API enqueues it and
    // writes a pending attempt) — keep refreshing until it resolves so the
    // score shows up here without closing/reopening the modal.
    // This video's pages and unclaimed ones only — the scope the list itself had
    // before it became a picker. usePendingScorePoll has NO staleness bound, so a
    // dead pending attempt on another video's page would otherwise keep this modal
    // refetching every 5s for as long as it stays open.
    const pollRows = useMemo(
        () => pdpUrls.filter((u) => pageAssignment(u, video?.id) !== "other"),
        [pdpUrls, video?.id]
    );
    usePendingScorePoll(pollRows, refreshPdpUrlsSilently);

    const handleRescore = async () => {
        if (!desireid || isAddTab || !current) return;
        setRescoring(true);
        const rowId = current.id;
        const prevMarkup = markupSignature(current);
        try {
            const { data } = await axiosInstant.post(rescoreProductPdpUrl(desireid, rowId));
            const body = data?.latest_score ? data : data?.data;
            const fresh: PdpUrlScore | null = body?.latest_score || null;
            if (fresh) {
                // Same latest_score / latest_attempt shape as the list response.
                const attempt: PdpUrlAttempt | null = body?.latest_attempt || null;
                setPdpUrls((prev) =>
                    prev.map((u) => (u.id === rowId ? { ...u, latest_score: fresh, latest_attempt: attempt } : u))
                );
            }
            toast.success("Successfully rescored PDP URL!");
            markupWatchCancel.current?.();
            markupWatchCancel.current = watchMarkupChange({
                rowId,
                prev: prevMarkup,
                load: refreshPdpUrlsSilently,
                onChange: () => toast.success("JSON-LD updated from the analysis")
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't rescore this PDP URL. Please try again.");
            refreshPdpUrlsSilently();
        } finally {
            setRescoring(false);
        }
    };

    // Brand users can't run the sync rescore (internal-only): they request a
    // report, the same "Run PDP Analysis" Product Pages offers.
    const handleRequestScore = async () => {
        if (!desireid || isAddTab || !current) return;
        setRescoring(true);
        const rowId = current.id;
        try {
            const { data } = await axiosInstant.post(requestProductPdpUrlScore(desireid, rowId));
            const body = data?.latest_attempt ? data : data?.data;
            const attempt: PdpUrlAttempt | null = body?.latest_attempt || null;
            if (attempt) setPdpUrls((prev) => prev.map((u) => (u.id === rowId ? { ...u, latest_attempt: attempt } : u)));
            else refreshPdpUrlsSilently();
            toast.success("Report started — the score updates here when it finishes.");
        } catch (e: any) {
            const status = e?.response?.status;
            if (status === 409) {
                toast("A report is already running for this page.");
                refreshPdpUrlsSilently();
            } else if (status === 429) {
                toast.error("You've reached the hourly limit for PDP reports. Try again later.");
            } else {
                toast.error("Couldn't start the report. Please try again.");
            }
        } finally {
            setRescoring(false);
        }
    };

    // The shared page summary changed the row (scoring, URL / SKUs, a video
    // taken off, or the row deleted): reload, staying on the row when it
    // still exists.
    const handleSummaryChanged = async ({ deleted }: { deleted?: boolean }) => {
        if (!desireid || !current) return;
        if (deleted) {
            await fetchPdpUrls(desireid);
            return;
        }
        await refetchAndLand(current.id);
    };

    // Poll for background JSON-LD generation after a save with video
    // context. Ends on json_ld change (success) or after 90s (the video
    // likely has no transcript yet — tell the user instead of spinning).
    useEffect(() => {
        if (!autoGenWatch || !desireid) return undefined;
        let cancelled = false;
        const started = Date.now();
        setGeneratingJsonLd(true);
        const finish = () => {
            if (cancelled) return;
            setGeneratingJsonLd(false);
            setAutoGenWatch(null);
        };
        const tick = async () => {
            if (cancelled) return;
            try {
                const { data } = await axiosInstant.get(listProductPdpUrls(desireid));
                const rows: PdpUrl[] = Array.isArray(data) ? data : data?.data || [];
                const row = rows.find((r) => r.id === autoGenWatch.rowId);
                const fresh = linkFor(row, video?.id)?.json_ld;
                if (fresh && JSON.stringify(fresh) !== autoGenWatch.prev) {
                    setPdpUrls(rows); // keep pdpIdx — row order (id ASC) is stable
                    finish();
                    toast.success("JSON-LD generated!");
                    return;
                }
            } catch (e) {
                // transient poll failure — keep trying until the deadline
            }
            if (Date.now() - started > 90000) {
                finish();
                toast("JSON-LD wasn't generated automatically — the video may not have a transcript yet. Use Generate once it does.", { icon: "ℹ️" });
                return;
            }
            setTimeout(tick, 4000);
        };
        tick();
        return () => {
            cancelled = true;
            setGeneratingJsonLd(false);
        };
    }, [autoGenWatch]);

    useEffect(() => {
        if (desireid) fetchPdpUrls(desireid);
    }, [desireid, brandId]);

    useEffect(() => {
        if (!desireid || pdpBrand.workspaceBrandId) {
            setProductBrands([]);
            return undefined;
        }
        let cancelled = false;
        axiosInstant
            .get(getProductDetail(desireid))
            .then(({ data }) => {
                const body = data?.brands ? data : data?.data;
                if (!cancelled) setProductBrands(Array.isArray(body?.brands) ? body.brands : []);
            })
            .catch(() => {
                if (!cancelled) setProductBrands([]);
            });
        return () => {
            cancelled = true;
        };
    }, [desireid, pdpBrand.workspaceBrandId]);

    useEffect(() => {
        if (isAddTab) {
            setPdpUrlDraft("");
            setVideoFileUrlDraft("");
            setOverrideDrafts(EMPTY_OVERRIDES);
            setSkusDraft([]);
        } else {
            setPdpUrlDraft(current?.pdp_url || "");
            setVideoFileUrlDraft(currentLink?.video_file_url || "");
            setOverrideDrafts(overridesFromLink(currentLink));
            setSkusDraft(current?.skus || []);
        }
    }, [pdpIdx, pdpUrls]);

    const handleSave = async () => {
        if (!desireid) return;
        if (isAddTab && !newRowBrandId) {
            toast.error("Select the brand this page belongs to.");
            return;
        }
        if (isAddTab && !pdpUrlDraft.trim()) {
            toast.error("Please enter a PDP URL before saving.");
            return;
        }
        setSaving(true);
        // Untouched fields are not sent, so a row keeps following our derived
        // value instead of freezing today's copy as an override. A cleared
        // field is sent as "" — the API stores null and generation falls
        // back to our default again.
        const prefilled = overridesFromLink(isAddTab ? null : currentLink);
        const overrides = Object.fromEntries(
            Object.entries(overrideDrafts)
                .filter(([field, value]) => value.trim() !== prefilled[field as keyof typeof EMPTY_OVERRIDES])
                .map(([field, value]) => [field, value.trim()])
        );
        // The API auto-generates this video's JSON-LD (fire-and-forget) when
        // its link has a file URL and an anchor (the page URL, the file URL
        // or an override) actually changed — mirror that condition to know
        // whether to watch for the result.
        const savedUrl = isAddTab ? pdpUrlDraft.trim() : current?.pdp_url || "";
        const savedVideoFileUrl = videoFileUrlDraft.trim() || null;
        const linkChanged =
            !isAddTab &&
            assignment === "mine" &&
            (savedVideoFileUrl !== (currentLink?.video_file_url || null) || Object.keys(overrides).length > 0);
        // Only a page this video is on regenerates on Save: the server skips
        // links with no file URL, and Save no longer adds the video to a page
        // (AssignPageBlock does).
        const wantsAutoGen =
            !!video?.id && !!savedVideoFileUrl && (isAddTab || (assignment === "mine" && linkChanged));
        const prevJson = isAddTab ? "null" : linkJson(current);
        try {
            if (isAddTab) {
                await axiosInstant.post(createProductPdpUrl(desireid), {
                    pdp_url: savedUrl,
                    video_file_url: savedVideoFileUrl,
                    brand_id: newRowBrandId,
                    skus: skusDraft,
                    // Put the video it was created from on the page — its link
                    // carries the file URL and overrides typed here.
                    s3_file_id: video?.id || null,
                    ...overrides
                });
                toast.success(
                    skusDraft.length
                        ? "Successfully added PDP URL and SKUs!"
                        : "Successfully added PDP URL!"
                );
            } else {
                // Only this video's link on the page (file URL, overrides):
                // the page's URL / SKUs are edited from the page summary
                // above, like on Product Pages. Adding the video to a page is
                // its own action (AssignPageBlock / OtherVideoSummary).
                if (linkChanged && video?.id) {
                    await axiosInstant.put(productPdpUrlVideo(desireid, current.id, video.id), {
                        video_file_url: savedVideoFileUrl,
                        ...overrides
                    });
                }
                toast.success("Saved this video's details for the page.");
            }
            const rows = await fetchPdpUrls(desireid);
            if (rows) {
                const savedIdx = isAddTab
                    ? rows.findIndex((r) => r.pdp_url === savedUrl)
                    : rows.findIndex((r) => r.id === current.id);
                const savedRow = savedIdx >= 0 ? rows[savedIdx] : null;
                // Land on the row just saved (fetchPdpUrls resets to the first
                // tab) — for a new URL that's where its scoring progress shows.
                if (savedIdx >= 0) setPdpIdx(savedIdx);
                if (wantsAutoGen && savedRow) {
                    setAutoGenWatch({ rowId: savedRow.id, prev: prevJson });
                }
            }
        } catch (e: any) {
            if (e?.response?.status === 409) {
                toast.error("That PDP URL already exists on this product.");
            } else {
                toast.error(e?.response?.data?.message || "Couldn't save PDP URL. Please try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateJsonLd = async () => {
        if (!video?.id || isAddTab || !current) return;
        setGeneratingJsonLd(true);
        try {
            const res = await axiosInstant.get(
                getFileIconikJsonLd(video.id, {
                    pdpUrlId: current.id,
                    regenerate: true
                })
            );
            toast.success("Successfully generated JSON-LD!");
            // Generating does not only write json_ld: the endpoint puts this
            // video on the page when it was not yet (iconik.js links it), so
            // the chip and the form change. Re-read instead of patching one
            // field.
            await refetchAndLand(current.id);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't generate JSON-LD. Please try again.");
        } finally {
            setGeneratingJsonLd(false);
        }
    };

    // Product Object from the product record — works on any page, assigned
    // or not. Patched in place; nothing else on the row changes.
    const handleGenerateProductJsonLd = async () => {
        if (!desireid || isAddTab || !current) return;
        setGeneratingProduct(true);
        try {
            const { data } = await axiosInstant.post(generateProductPdpUrlJsonLd(desireid, current.id));
            const body = data?.product_json_ld ? data : data?.data;
            setPdpUrls((prev) =>
                prev.map((u) =>
                    u.id === current.id
                        ? {
                              ...u,
                              product_json_ld: body?.product_json_ld ?? u.product_json_ld,
                              product_json_ld_validation: body?.product_json_ld_validation ?? u.product_json_ld_validation
                          }
                        : u
                )
            );
            toast.success("Product Object generated!");
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Couldn't generate the Product Object. Please try again.");
        } finally {
            setGeneratingProduct(false);
        }
    };

    // After adding the video from the block or the summary: refetch (the
    // chips change), land on the same row by id, and watch for the JSON-LD
    // the server may now be generating for this video.
    const landOnAssigned = async (pageId: number) => {
        const prevJson = linkJson(pdpUrls.find((u) => u.id === pageId));
        const row = await refetchAndLand(pageId);
        // The watch condition mirrors the server's rule (maybeGenerateJsonLd:
        // active row + a link with a video file URL) the way handleSave's
        // `wantsAutoGen` does. A looser test leaves the pane greyed for the full
        // 90s on a generation the server never attempted, then blames a missing
        // transcript — which may not be the reason at all.
        const link = linkFor(row, video?.id);
        if (row && link && link.video_file_url && row.active !== false) {
            setAutoGenWatch({ rowId: row.id, prev: prevJson });
        }
    };

    if (products.length === 0) {
        return <p className="text-sm text-muted py-4">No products linked to this video.</p>;
    }

    const labelCls = "block text-[12px] font-semibold text-text-2 uppercase tracking-[0.3px] mb-[7px]";
    const inputWrapCls =
        "flex items-center gap-[9px] px-[14px] py-[11px] border border-border-token rounded-[10px] bg-surface transition-colors focus-within:border-accent";
    const inputCls =
        "field-input flex-1 bg-transparent outline-none text-[13.5px] text-text-1 min-w-0 placeholder:text-muted";

    // The background watch is row-scoped — only surface the generating
    // state on the row actually being generated (manual Generate always
    // targets the current row, so it has no watch).
    const showGenerating =
        generatingJsonLd && (!autoGenWatch || autoGenWatch.rowId === current?.id);

    return (
        <Box className="flex flex-col gap-4 w-full">
            <Box>
                <span className="block text-[11px] font-bold text-muted uppercase tracking-[0.5px] mb-[7px]">
                    Product
                </span>
                <Box className="flex flex-wrap gap-2">
                    {products.map((p: any, i: number) => (
                        <button
                            key={i}
                            type="button"
                            title={p.title || `Product ${i + 1}`}
                            onClick={() => setProductIdx(i)}
                            className={classNames(
                                "text-[14px] font-semibold px-[15px] py-2 rounded-[9px] border max-w-[280px] truncate transition-colors",
                                i === productIdx
                                    ? "bg-tint border-transparent text-accent-text"
                                    : "bg-surface border-border-token text-text-2 hover:border-accent"
                            )}
                        >
                            {p.title || `Product ${i + 1}`}
                        </button>
                    ))}
                </Box>
            </Box>

            {loading ? (
                <DotLottleLoader fill={false} />
            ) : (
                <Box className="border border-border-token rounded-[14px] p-[18px]">
                    {product && (
                        <Box className="flex items-center justify-between gap-3 flex-wrap mb-4 pb-4 border-b border-border-token">
                            <Box className="flex items-center gap-3 min-w-0">
                                {product.image ? (
                                    <img
                                        src={product.image}
                                        alt={product.title || "Product"}
                                        className="w-14 h-14 rounded-[10px] object-cover border border-border-token bg-surface-2 shrink-0"
                                    />
                                ) : (
                                    <Box className="w-14 h-14 rounded-[10px] border border-border-token bg-surface-2 text-muted flex items-center justify-center shrink-0">
                                        {/* stroke is an SVG attribute and cannot read var(); inherit --muted via currentColor */}
                                        <IconProduct width={24} height={24} stroke="currentColor" />
                                    </Box>
                                )}
                                <span className="text-[14px] font-semibold text-text-1 truncate max-w-[420px]">
                                    {product.title || `Product ${productIdx + 1}`}
                                </span>
                            </Box>
                            {productHref && (
                                <a
                                    href={productHref}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-[6px] px-3 py-[7px] rounded-lg text-[12.5px] font-semibold text-text-2 bg-surface border border-border-token hover:border-accent hover:text-text-1 transition-colors shrink-0"
                                >
                                    <IconGoToTab width={14} height={14} stroke="currentColor" /> View product
                                </a>
                            )}
                        </Box>
                    )}
                    <Box className="inline-flex flex-wrap gap-[3px] bg-surface-2 rounded-[10px] p-1 mb-[18px]">
                        {pdpUrls.map((u, i) => (
                            <button
                                key={u.id}
                                type="button"
                                title={u.pdp_url}
                                onClick={() => setPdpIdx(i)}
                                className={classNames(
                                    "inline-flex items-center gap-[6px] px-4 py-[7px] rounded-[7px] text-[13px] font-semibold transition-colors",
                                    pdpIdx === i
                                        ? "bg-surface text-text-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                                        : "text-muted hover:text-text-2"
                                )}
                            >
                                PDP {i + 1}
                                {u.retailer && (
                                    <span className="text-[10px] font-bold text-muted uppercase tracking-[0.4px]">
                                        {u.retailer}
                                    </span>
                                )}
                                <AssignmentChip status={pageAssignment(u, video?.id)} />
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setPdpIdx(pdpUrls.length)}
                            className={classNames(
                                "inline-flex items-center gap-[5px] px-[14px] py-[7px] rounded-[7px] text-[13px] font-semibold transition-colors",
                                isAddTab
                                    ? "bg-surface text-text-1 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                                    : "text-text-2 hover:text-text-1"
                            )}
                        >
                            <IconPlus width={13} height={13} stroke="currentColor" /> Add PDP URL & SKUs
                        </button>
                    </Box>

                    {assignment === "other" && current && desireid != null ? (
                        <OtherVideoSummary
                            desireid={desireid}
                            page={current}
                            videoId={video?.id}
                            onAssigned={landOnAssigned}
                        />
                    ) : (
                    <>
                    {!isAddTab && current && desireid != null && (
                        <Box className="mb-6">
                            {/* The page itself — the same summary Product Pages shows. */}
                            <PdpPageSummary
                                row={current}
                                desireid={desireid}
                                currentVideoId={video?.id}
                                onChanged={handleSummaryChanged}
                            />
                            <PdpScoreStatus
                                className="mb-3"
                                score={current.latest_score}
                                attempt={current.latest_attempt}
                                onRescore={isInternal(role) ? handleRescore : handleRequestScore}
                                rescoring={rescoring}
                                rescoreLabel={isInternal(role) ? undefined : "Run PDP Analysis"}
                            />
                            <PdpScoreHistory
                                className="mt-4"
                                productId={desireid}
                                pdpUrlId={current.id}
                                pdpUrl={current.pdp_url}
                                latestScoreId={current.latest_score?.id}
                                onViewReport={(scoreId) =>
                                    setViewingReport({ pdpUrlId: current.id, scoreId, pdpUrl: current.pdp_url })
                                }
                            />
                        </Box>
                    )}

                    {!isAddTab && current && (
                        <PdpJsonLdOutput
                            className="mb-6 pt-3 pb-5 border-t border-b border-border-token"
                            row={current}
                            ownFileId={video?.id}
                            eligibilitySize="lg"
                            // Generation reads THIS video's transcript and puts the video
                            // on the page when it was not yet (see handleGenerateJsonLd),
                            // so it is offered on a page with no video too.
                            onGenerate={handleGenerateJsonLd}
                            generating={showGenerating}
                            onGenerateProduct={handleGenerateProductJsonLd}
                            generatingProduct={generatingProduct}
                        />
                    )}

                    <Box className="flex flex-col">
                        {isAddTab && (
                            <p className="text-[12px] text-text-2 mb-4">This video will be put on the new page.</p>
                        )}
                        {assignment === "unassigned" && current && desireid != null && (
                            <AssignPageBlock
                                desireid={desireid}
                                page={current}
                                videoId={video?.id}
                                onAssigned={landOnAssigned}
                            />
                        )}
                        {isAddTab && (
                            <>
                                <FieldLabel help={PDP_FIELD_HELP.pdpUrl}>PDP URL</FieldLabel>
                                <Box className={classNames(inputWrapCls, "mb-4")}>
                                    <IconLink size={15} stroke="#B5AFA5" />
                                    <input
                                        className={inputCls}
                                        placeholder="https://retailer.com/p/product…"
                                        value={pdpUrlDraft}
                                        onChange={(e) => setPdpUrlDraft(e.target.value)}
                                    />
                                </Box>
                            </>
                        )}
                        {!isAddTab && assignment === "mine" && (
                            <span className={labelCls}>This video on the page</span>
                        )}

                        {/* This video's details on the page — only once it is on it
                            (or on the Add tab, where it will be). */}
                        {(isAddTab || assignment === "mine") && (
                            <>
                                <FieldLabel help={PDP_FIELD_HELP.videoFileUrl}>PDP Video File URL</FieldLabel>
                                <Box className={classNames(inputWrapCls, "mb-4")}>
                                    <IconVideo size={15} stroke="#B5AFA5" />
                                    <input
                                        className={inputCls}
                                        placeholder="https://cdn…/video.mp4"
                                        value={videoFileUrlDraft}
                                        onChange={(e) => setVideoFileUrlDraft(e.target.value)}
                                    />
                                </Box>
                            </>
                        )}

                        {isAddTab && !pdpBrand.workspaceBrandId && pdpBrand.options.length > 0 && (
                            <>
                                <FieldLabel help={PDP_FIELD_HELP.brand}>Brand</FieldLabel>
                                <Box className="mb-4">
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={pdpBrand.picked}
                                        onChange={(e) => pdpBrand.setPicked(e.target.value)}
                                        helperText="Whose page this is — the retailer for a retail listing, the supplier for its own site."
                                    >
                                        {pdpBrand.options.map((b) => (
                                            <MenuItem key={b.id} value={String(b.id)}>
                                                {brandOptionLabel(b)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </>
                        )}

                        {!isAddTab &&
                            assignment === "mine" &&
                            OVERRIDE_FIELDS.map(({ field, label: fieldLabel, isDate, help }) => (
                                <React.Fragment key={field}>
                                    <FieldLabel help={help}>{fieldLabel}</FieldLabel>
                                    {isDate ? (
                                        <Box className="mb-4">
                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                                adapterLocale="en-us"
                                            >
                                                <DatePicker
                                                    value={
                                                        overrideDrafts[field]
                                                            ? dayjs(overrideDrafts[field])
                                                            : null
                                                    }
                                                    onChange={(next: any) =>
                                                        setOverrideDrafts((prev) => ({
                                                            ...prev,
                                                            [field]: next
                                                                ? next.format("YYYY-MM-DD")
                                                                : ""
                                                        }))
                                                    }
                                                    slotProps={{
                                                        textField: { fullWidth: true, size: "small" },
                                                        popper: { placement: "bottom-end" }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                    ) : (
                                        <Box className={classNames(inputWrapCls, "mb-4")}>
                                            <input
                                                className={inputCls}
                                                value={overrideDrafts[field]}
                                                onChange={(e) =>
                                                    setOverrideDrafts((prev) => ({
                                                        ...prev,
                                                        [field]: e.target.value
                                                    }))
                                                }
                                            />
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                        {isAddTab && (
                            <Box className="mb-4">
                                <SkuPdpLinkEditor
                                    value={skusDraft}
                                    onChange={setSkusDraft}
                                    disabled={saving}
                                />
                            </Box>
                        )}

                        {/* Add: the new page. Existing page: this video's details
                            on it (URL, SKUs, removing the video and deleting the
                            page live in the summary above). */}
                        {(isAddTab || assignment === "mine") && (
                            <Box className="flex gap-[10px] mt-[2px]">
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-[22px] py-[10px] rounded-[9px] text-[13.5px] font-semibold text-white bg-accent hover:bg-[#967435] transition-colors disabled:opacity-60"
                                >
                                    {isAddTab ? "Add" : "Save"}
                                </button>
                            </Box>
                        )}
                    </Box>

                    </>
                    )}
                </Box>
            )}

            {viewingReport && desireid != null && (
                <PdpScoreReportDialog
                    productId={desireid}
                    pdpUrlId={viewingReport.pdpUrlId}
                    scoreId={viewingReport.scoreId}
                    pdpUrl={viewingReport.pdpUrl}
                    onClose={() => setViewingReport(null)}
                />
            )}

        </Box>
    );
}
